import {
    IDataRequest,
    IDataRequestFilters,
    IDataRequestPermissions,
    IDataRequestsByType,
    IDataRequestSummary
} from '@vivli/features/data-requests/infrastructure/interface';
import {
    ApprovalStatusEnum,
    DataRequestStatusEnum,
    DataRequestType,
    StudyRequestStatusEnum
} from '@vivli/features/data-requests/infrastructure/enum';
import { IOrganizationRoles, IUser } from '@vivli/shared/infrastructure/interface';
import { PermissionsEnum } from '@vivli/shared/infrastructure/enum';
import { useAssignedAppTypeContext } from '@vivli/core/infrastructure/context';

export const useDataRequestFilters = (dataRequestPermissions: IDataRequestPermissions): IDataRequestFilters => {
    const { isAmr } = useAssignedAppTypeContext();

    const hasBeenSubmittedFilter = (dr: IDataRequestSummary | IDataRequest) => !!dr.submittedTime;

    const withdrawnFilter = (dr) => dr.status === DataRequestStatusEnum.Cancelled;

    const archivedFilter = (dr) => dr.status === DataRequestStatusEnum.Archived;

    const notApprovedFilter = (dr) =>
        dr.status === DataRequestStatusEnum.RejectedByVivli ||
        dr.status === DataRequestStatusEnum.RejectedByIRP ||
        dr.status === DataRequestStatusEnum.RejectedByDataProvider ||
        dr.status === DataRequestStatusEnum.RevisionsRequestedByDataProvider ||
        dr.status === DataRequestStatusEnum.RevisionsRequestedByIRP;

    const fulfilledFilter = (dr) => dr.status === DataRequestStatusEnum.Fulfilled;

    const activeFilter = (dr: IDataRequestSummary | IDataRequest) =>
        dr.status === DataRequestStatusEnum.Approved ||
        dr.status === DataRequestStatusEnum.AwaitingDUAValidation ||
        dr.status === DataRequestStatusEnum.DUAReturnedToRequester ||
        dr.status === DataRequestStatusEnum.DUAValidated ||
        dr.status === DataRequestStatusEnum.PartiallyFulfilled ||
        dr.status === DataRequestStatusEnum.SubmittedToVivli ||
        dr.status === DataRequestStatusEnum.AwaitingDataProviderApproval ||
        dr.status === DataRequestStatusEnum.AwaitingIRPApproval ||
        dr.status === DataRequestStatusEnum.Fulfilled ||
        (dr.status === DataRequestStatusEnum.Draft && hasBeenSubmittedFilter(dr));

    const draftFilter = (dr) => dr.status === DataRequestStatusEnum.Draft && !hasBeenSubmittedFilter(dr);

    const isDataProvider = (om: IOrganizationRoles) => {
        return om.vivliPermissions?.includes(PermissionsEnum.DataContrib);
    };

    const isOrgAdmin = (om: IOrganizationRoles) => {
        return om.vivliPermissions?.includes(PermissionsEnum.ProviderActAdv);
    };

    const awaitingUserActionFilter = (user: IUser) => (dr) => {
        const isVivliAdmin = user.vivliPermissions.includes(PermissionsEnum.DrActAdv);
        //TODO PERMFE - redo with permissions+
        switch (dr.status) {
            case DataRequestStatusEnum.SubmittedToVivli:
            case DataRequestStatusEnum.AwaitingDUAValidation:
            case DataRequestStatusEnum.Approved:
                //TODO PERMFE - redo with permissions+
                return isVivliAdmin;
            case DataRequestStatusEnum.AwaitingDataProviderApproval:
                //TODO PERMFE - redo with permissions+
                return dr.requiredDataProviderApprovals.some(
                    (dpa) =>
                        dpa.status === ApprovalStatusEnum.None &&
                        dataRequestPermissions.isOrgMember(
                            dpa.orgId,
                            (om) =>
                                om.vivliPermissions?.includes(PermissionsEnum.DataContrib) ||
                                (om.vivliPermissions?.includes(PermissionsEnum.ProviderActAdv) && !isVivliAdmin)
                        )
                );
            case DataRequestStatusEnum.AwaitingIRPApproval:
                //TODO PERMFE - redo with permissions+
                return dr.requiredIRPApprovals.some(
                    (dpa) =>
                        dpa.status === ApprovalStatusEnum.None &&
                        dataRequestPermissions.isOrgMember(
                            dpa.orgId,
                            (om) =>
                                om.vivliPermissions?.includes(PermissionsEnum.IRPApprove) ||
                                (om.vivliPermissions?.includes(PermissionsEnum.ProviderActAdv) && !isVivliAdmin)
                        )
                );
            case DataRequestStatusEnum.DUAValidated:
            case DataRequestStatusEnum.PartiallyFulfilled:
                //only user actions awaited at these statuses are for
                // DRs that I need to upload data for
                //TODO PERMFE - redo with permissions+
                const awaitingIpdIpdOrgIds = dr.requestedStudies
                    .filter((s) => s.studyRequestStatus === StudyRequestStatusEnum.New)
                    .map((a) => a.organizationId);
                let awaitingMeToUploadIpd = awaitingIpdIpdOrgIds?.some((o) =>
                    dataRequestPermissions.isOrgMember(o, (om) => om.vivliPermissions?.includes(PermissionsEnum.DataContrib))
                );

                //in Amr mode, Vivli Admins should also see drs awaiting IPD upload
                if (isAmr && awaitingIpdIpdOrgIds?.length > 0 && isVivliAdmin) {
                    awaitingMeToUploadIpd = true;
                }
                return awaitingMeToUploadIpd;
            default:
                return false;
        }
    };

    const getFilterByType = (type: DataRequestType, user: IUser) => {
        switch (type) {
            case 'archived':
                return archivedFilter;
            case 'cancelled':
                return withdrawnFilter;
            case 'draft':
                return draftFilter;
            case 'fulfilled':
                return fulfilledFilter;
            case 'active':
                return activeFilter;
            case 'notApproved':
                return notApprovedFilter;
            case 'awaitingUserAction':
                return awaitingUserActionFilter(user);
        }
    };

    const filterRequestsByType = (dataRequests: IDataRequestSummary[], type: DataRequestType, user: IUser) => {
        return dataRequests.filter(getFilterByType(type, user));
    };

    const filterAllRequestsByType = (dataRequests: IDataRequestSummary[], user: IUser): IDataRequestsByType => {
        const draftRequests = dataRequests.filter(draftFilter);
        const active = dataRequests.filter(activeFilter);
        const notApproved = dataRequests.filter(notApprovedFilter);
        const withdrawn = dataRequests.filter(withdrawnFilter);
        const archived = dataRequests.filter(archivedFilter);

        let result: IDataRequestsByType = {
            draftRequests,
            active,
            notApproved,
            withdrawn,
            archived
        };

        if (user) {
            const awaitingUserAction = dataRequests.filter(awaitingUserActionFilter(user));

            result = {
                ...result,
                awaitingUserAction
            };
        }

        return result;
    };

    const filterAwaitingUserActionRequests = (dataRequests: IDataRequestSummary[], user: IUser): IDataRequestSummary[] => {
        return filterRequestsByType(dataRequests, 'awaitingUserAction', user);
    };

    const filterAwaitingUpload = (dataRequests: IDataRequestSummary[]): IDataRequestSummary[] => {
        return dataRequests.filter(
            (r) => r.status === DataRequestStatusEnum.DUAValidated || r.status === DataRequestStatusEnum.PartiallyFulfilled
        );
    };

    return {
        hasBeenSubmittedFilter,
        withdrawnFilter,
        archivedFilter,
        notApprovedFilter,
        fulfilledFilter,
        activeFilter,
        draftFilter,
        isDataProvider,
        isOrgAdmin,
        awaitingUserActionFilter,
        getFilterByType,
        filterRequestsByType,
        filterAllRequestsByType,
        filterAwaitingUserActionRequests,
        filterAwaitingUpload
    };
};
